import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import {
  attributeHeaderStyle,
  categoryHeaderStyle,
  headingStyle,
  infoBannerStyle,
  subCategoryHeaderStyle,
  warpText,
  BULK_TEXT,
} from '../constants/bulkExport'

/* Function to convert inter to Excel cell position */
const covertToExcelPosition = (num) => {
  const res = String.fromCharCode(65 + (num % 26))
  return num >= 26 ? covertToExcelPosition(Math.floor(num / 26) - 1) + res : res
}

export const flattenObject = (attributeObj) => {
  const flattened = []
  const flattenHelper = (attributeValues) => {
    const flattenedObj = []
    Object.entries(attributeValues).forEach(([, value]) => {
      if (Array.isArray(value)) {
        if (Array.isArray(value[0])) {
          value.forEach((item) => {
            const arrayVal = item.join().split('::')
            if (arrayVal[1]) {
              flattenedObj.push(arrayVal[1].replace(/[\[\]']+/g, '')) //eslint-disable-line
            }
          })
        } else {
          flattenedObj.push(value.join(', '))
        }
      } else {
        flattenedObj.push(value)
      }
    })
    flattened.push(flattenedObj)
  }
  attributeObj.forEach((item) => {
    flattenHelper(item)
  })

  return flattened
}
const sheetName = (itemType) => {
  if (!itemType || typeof itemType !== 'string') {
    return 'Sheet' // default fallback name
  }
  const name = itemType.trim()
  return name.length <= 31 ? name : name.substring(0, 28) + '...'
}

const constructExcelDataMultiSheet = async (workbook, data) => {
  const catStart = 3 // Category row
  const subCatStart = 4 // Sub-category row
  const attributeHeaderRow = 5 // Attribute header row
  data.forEach((sheetData) => {
    const cutSheetName = sheetName(sheetData.itemType)
    const dataSheet = workbook.addWorksheet(`${cutSheetName}`, {
      properties: {
        defaultColWidth: 20,
      },
      views: [
        {
          state: 'frozen',
          xSplit: 3,
          ySplit: attributeHeaderRow - 1,
          zoomScale: 120,
          activeCell: 'D5', // Point the cursor
        },
      ],
    })

    const attributeCount = sheetData.attributeName.length
    const lastCol = covertToExcelPosition(Math.max(attributeCount - 1, 0))

    // Row 1 - Heading (merged across all attribute columns)
    dataSheet.mergeCells(`A1:${lastCol}1`)
    const headingCell = dataSheet.getCell('A1')
    headingCell.value = BULK_TEXT.EXPORT.HEADING
    headingCell.font = headingStyle.font
    headingCell.alignment = headingStyle.alignment
    dataSheet.getRow(1).height = 22

    // Row 2 - Info banner (merged across all attribute columns)
    dataSheet.mergeCells(`A2:${lastCol}2`)
    const bannerCell = dataSheet.getCell('A2')
    bannerCell.value = BULK_TEXT.EXPORT.INFO_BANNER
    bannerCell.font = infoBannerStyle.font
    bannerCell.fill = infoBannerStyle.fill
    bannerCell.alignment = infoBannerStyle.alignment
    dataSheet.getRow(2).height = 28

    /* Iterate categories */
    sheetData.categoriesObj.forEach((category) => {
      const catCellStart = covertToExcelPosition(category.start)
      const catCellEnd = covertToExcelPosition(category.end)
      const catCell = `${catCellStart}${catStart}:${catCellEnd}${catStart}`
      dataSheet.mergeCells(catCell)
      if (category.displayName !== '') {
        const getCell = dataSheet.getCell(`${catCellStart}${catStart}`)
        getCell.value = category.displayName.split('_').join(' ').toUpperCase()
        getCell.fill = categoryHeaderStyle.fill
        getCell.font = { ...categoryHeaderStyle.font }
        getCell.alignment = categoryHeaderStyle.alignment
        getCell.border = categoryHeaderStyle.border
      }
    })

    /* Iterate Sub categories */
    sheetData.subCategoriesObj.forEach((subCategory) => {
      const subCatCellStart = covertToExcelPosition(subCategory.start)
      const subCatCellEnd = covertToExcelPosition(subCategory.end)
      const subCatCell = `${subCatCellStart}${subCatStart}:${subCatCellEnd}${subCatStart}`
      dataSheet.mergeCells(subCatCell)
      const getCell = dataSheet.getCell(`${subCatCellStart}${subCatStart}`)
      getCell.value = subCategory.displayName
      getCell.fill = subCategoryHeaderStyle.fill
      getCell.font = { ...subCategoryHeaderStyle.font }
      getCell.alignment = subCategoryHeaderStyle.alignment
      getCell.border = subCategoryHeaderStyle.border
    })

    // Add the row with attribute header (no leading label column)
    const attributeHeader = dataSheet.addRow(
      sheetData.attributeName.map((row) => row.attributeDisplayName),
    )
    attributeHeader.font = attributeHeaderStyle.font
    attributeHeader.alignment = attributeHeaderStyle.alignment
    // Fill must be applied per-cell so the background covers every column,
    // including empty cells beyond the last populated attribute.
    const lastAttrCol = Math.max(sheetData.attributeName.length, 1)
    for (let col = 1; col <= lastAttrCol; col += 1) {
      const cell = attributeHeader.getCell(col)
      cell.fill = attributeHeaderStyle.fill
      cell.border = attributeHeaderStyle.border
    }

    // Add the data rows (no leading blank column)
    const rows = flattenObject(sheetData.attributeValues)
    rows.forEach((row) => {
      dataSheet.addRow(row)
    })

    // Loop thru all rows and set wrap text on data rows
    dataSheet.eachRow((row, rowNumber) => {
      if (rowNumber > attributeHeaderRow) {
        row.alignment = warpText
      }
    })
    applyColumnFilter(dataSheet, attributeHeaderRow, sheetData)
  })
}

export const applyColumnFilter = async (worksheet, headerRow, sheetData) => {
  const lastRow = worksheet.rowCount
  const lastColumn = Math.max(sheetData.attributeName.length, 1)
  worksheet.autoFilter = {
    from: `A${headerRow}`,
    to: {
      row: lastRow,
      column: lastColumn,
    },
  }
}

export const generateExportExcelMuLtiSheet = async (
  itemData,
  fileName,
  itemTypeLabel,
) => {
  let downloaded
  const data = itemData
  const workbook = new ExcelJS.Workbook()
  workbook.category = 'LaunchPad'
  workbook.lastModifiedBy = `LaunchPad`
  workbook.created = new Date()
  workbook.modified = new Date()
  workbook.views = [
    {
      x: 0,
      y: 0,
      width: 37000,
      height: 20000,
      firstSheet: 0,
      activeTab: 0,
      visibility: 'visible',
    },
  ]

  await constructExcelDataMultiSheet(workbook, data)
  await workbook.xlsx
    .writeBuffer()
    .then((buffer) => {
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `${fileName}.xlsx`,
      )
      downloaded = 'success'
    })
    .catch(() => {
      downloaded = 'failed'
    })
  return downloaded
}
